import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/utils/classUtils';
import { CalendarIcon, X } from 'lucide-react';
import { useState, type FC } from 'react';

interface DatePickerInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const START_MONTH = new Date(1990, 0);
const END_MONTH = new Date(2040, 11);

export const DatePickerInput: FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder = 'Pick a date',
}) => {
  const [open, setOpen] = useState(false);

  const dateValue = value ? new Date(value + 'T00:00:00') : undefined;

  const displayValue = dateValue
    ? dateValue.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '';

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      onChange(formatted);
    } else {
      onChange('');
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-start font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {displayValue || placeholder}
          {value && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onChange('');
              }}
              className="ml-auto shrink-0 rounded p-0.5 hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={dateValue}
          defaultMonth={dateValue}
          onSelect={handleSelect}
          startMonth={START_MONTH}
          endMonth={END_MONTH}
        />
      </PopoverContent>
    </Popover>
  );
};
